import { useTranslation } from "react-i18next";
import type { AllocationResponse } from "../services/workHoursService";
import { GapBadge, useExpectedRuleText } from "./ExpectedKpis";

// ---------------------------------------------------------------------------
// Asignación a proyectos (#573): reparto de la capacidad semanal por proyecto y
// horas planeadas contra horas reales en el rango. Desde #783 lo real son los
// minutos registrados por proyecto; en días sin entradas, el estimado por
// commits (se rotula aparte). Desde #843, lo trabajado fuera de la asignación
// (proyectos no asignados o repos sin proyecto) va en una línea extra
// «Fuera de asignación», por proyecto real y con su motivo. Solo lectura:
// el reparto lo edita un admin en Equipo → Colaboradores.
// ---------------------------------------------------------------------------

interface Props {
  data: AllocationResponse | null;
  isDarkMode: boolean;
  cardClass: string;
  textClass: string;
  secondaryClass: string;
}

export default function AllocationCard({ data, isDarkMode, cardClass, textClass, secondaryClass }: Props) {
  const { t } = useTranslation("common");
  const ruleText = useExpectedRuleText(data?.expected?.rule);
  if (!data) return null;

  return (
    <div className={cardClass} data-testid="allocation-card">
      <div className="mb-2 flex items-center justify-between">
        <p className={`text-sm font-semibold ${textClass}`}>{t("workHours.allocation.title")}</p>
        {data.teamMember && (
          <span className={`text-xs ${secondaryClass}`}>
            {t("workHours.allocation.capacity", { hours: data.teamMember.weeklyCapacityHours, pct: data.allocatedPercentage ?? 0 })}
          </span>
        )}
      </div>
      {!data.linked && <p className={`text-xs ${secondaryClass}`}>{t("workHours.allocation.notLinked")}</p>}
      {data.linked && data.allocations.length === 0 && <p className={`text-xs ${secondaryClass}`}>{t("workHours.allocation.none")}</p>}
      {data.plannedVsActual && (data.plannedVsActual.projects.length > 0 || (data.plannedVsActual.outOfAllocation?.hours ?? 0) > 0) && (
        <div className="space-y-3">
          {data.plannedVsActual.projects.map((p) => {
            const ratio = p.plannedHours > 0 ? p.actualHours / p.plannedHours : 0;
            return (
              <div key={p.projectId}>
                <div className={`flex justify-between text-xs ${textClass}`}>
                  <span className="font-medium">
                    {p.projectName} <span className={secondaryClass}>· {p.percentage}%</span>
                  </span>
                  <span>
                    {p.actualHours} / {p.plannedHours} h
                    {/* #783: parte estimada por commits (días sin entradas). */}
                    {!!p.estimatedHours && (
                      <span className="ml-1 text-[11px] text-amber-500">({t("workHours.allocation.estimatedPart", { hours: p.estimatedHours })})</span>
                    )}
                  </span>
                </div>
                <div className={`mt-1 h-2 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                  <div
                    className={`h-2 rounded-full ${ratio > 1 ? "bg-amber-500" : "bg-violet-500"}`}
                    style={{ width: `${Math.min(100, ratio * 100)}%` }}
                  />
                </div>
                {p.repos.length === 0 && <p className={`mt-0.5 text-[11px] ${secondaryClass}`}>{t("workHours.allocation.noRepos")}</p>}
              </div>
            );
          })}
          {data.plannedVsActual.outOfAllocation && data.plannedVsActual.outOfAllocation.hours > 0 && (
            <div data-testid="allocation-out-of-allocation" className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-2">
              <div className={`flex justify-between text-xs ${textClass}`}>
                <span className="font-medium text-orange-600" title={t("workHours.allocation.outOfAllocationHint")}>
                  <i className="ri-error-warning-line mr-1" />
                  {t("workHours.allocation.outOfAllocation")}
                </span>
                <span>
                  {t("workHours.allocation.outOfAllocationPart", {
                    hours: data.plannedVsActual.outOfAllocation.hours,
                    recorded: data.plannedVsActual.outOfAllocation.recordedHours,
                    estimated: data.plannedVsActual.outOfAllocation.estimatedHours,
                  })}
                </span>
              </div>
              <ul className="mt-1 space-y-1">
                {data.plannedVsActual.outOfAllocation.projects.map((p) => (
                  <li key={p.projectId} className={`text-[11px] ${secondaryClass}`}>
                    <span className={`font-medium ${textClass}`}>{p.projectLabel}</span> · {p.hours} h
                    {p.reasons.map((r) => (
                      <span key={r} className="block text-orange-600">
                        {r}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className={`text-[11px] ${secondaryClass}`}>
            {t("workHours.allocation.footer", {
              unassigned: data.plannedVsActual.unassignedHours,
              capacity: data.plannedVsActual.capacityHours,
            })}
          </p>
        </div>
      )}
      {/* #969: esperado a hoy (cortado a hoy) vs real, por proyecto. */}
      {data.linked && data.expected && (
        <div className={`mt-3 border-t pt-3 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`} data-testid="allocation-expected">
          <div className={`mb-1 flex items-center justify-between text-xs ${textClass}`}>
            <span className="flex items-center gap-1 font-semibold">
              {t("workHours.expected.allocationTitle")}
              <i className="ri-information-line cursor-help opacity-70" title={ruleText} aria-label={ruleText} data-testid="allocation-expected-rule" />
            </span>
            {data.expected.counted && (
              <span>
                {data.expected.actualHours} / {data.expected.expectedHours} h <GapBadge gapHours={data.expected.gapHours} testId="allocation-expected-gap" />
              </span>
            )}
          </div>
          {!data.expected.counted ? (
            <p className={`text-[11px] ${secondaryClass}`}>{t("workHours.expected.allocationNotCounted")}</p>
          ) : (
            <ul className="space-y-1">
              {data.expected.byProject.map((p) => (
                <li key={`${p.outOfAllocation ? "out" : "in"}-${p.projectId}`} className={`flex items-center justify-between gap-2 text-[11px] ${secondaryClass}`} data-testid={`allocation-expected-${p.projectId}`}>
                  <span>
                    <span className={`font-medium ${textClass}`}>{p.projectName}</span>
                    {p.outOfAllocation ? <span className="ml-1 text-orange-600">· {t("workHours.expected.outOfAllocation")}</span> : <span> · {p.percentage}%</span>}
                  </span>
                  <span className="flex items-center gap-1">
                    {t("workHours.expected.actual")} {p.actualHours} h · {t("workHours.expected.expected")} {p.expectedHours} h
                    <GapBadge gapHours={p.gapHours} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
